(() => {
  if (window.ReadywearPromoProgressLoaded) return;
  window.ReadywearPromoProgressLoaded = true;

  const defaults = {
    shippingThreshold: 15000,
    promoRequired: 4,
    eligibleKeyword: 'Graphic T-shirt',
    shippingNote: 'Ships in 3-5 days via DHL',
    accentColor: '#ef4f45',
    inkColor: '#1f1f1f',
  };

  let refreshTimer;
  let stickyInitialized = false;

  const money = (cents) => `RM${(Math.max(0, cents) / 100).toFixed(2)}`;

  const configFromElement = (element) => {
    if (!element) return defaults;

    return {
      shippingThreshold: Number(element.dataset.shippingThreshold || defaults.shippingThreshold),
      promoRequired: Number(element.dataset.promoRequired || defaults.promoRequired),
      eligibleKeyword: element.dataset.eligibleKeyword || defaults.eligibleKeyword,
      shippingNote: element.dataset.shippingNote || defaults.shippingNote,
      accentColor: element.dataset.accentColor || defaults.accentColor,
      inkColor: element.dataset.inkColor || defaults.inkColor,
    };
  };

  const getBaseConfig = () => configFromElement(document.querySelector('readywear-promo-progress'));

  const eligibleQuantity = (cart, keyword) => {
    const needle = String(keyword || '').toLowerCase().trim();
    if (!needle) return 0;

    return cart.items.reduce((total, item) => {
      const text = [item.product_title, item.title, item.product_type, item.handle]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return text.includes(needle) ? total + item.quantity : total;
    }, 0);
  };

  const shippingMessage = (cart, config) => {
    const remaining = config.shippingThreshold - cart.total_price;
    if (remaining <= 0) return `You're getting <strong>FREE</strong> shipping`;
    return `You're ${money(remaining)} away from <strong>FREE</strong> shipping`;
  };

  const promoMessage = (eligible, config) => {
    const remaining = config.promoRequired - eligible;
    if (remaining <= 0) return `Your <strong>FREE</strong> tee is unlocked`;
    const label = remaining === 1 ? 'tee' : 'tees';
    return `Add ${remaining} more ${label} for 1 <strong>FREE</strong> tee`;
  };

  const icon = (type) => {
    if (type === 'shipping') {
      return `
        <svg class="rw-promo-progress__icon-svg" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
          <path class="rw-promo-progress__icon-accent" d="M28.5 15.5h9.4l3.8 6.3v6.4h-4.5" />
          <path d="M7.2 11.5h5.1l3.6 18.1h20.7" />
          <path d="M15.4 17.2h16.4c1.2 0 2.1 1.1 1.8 2.3l-1.7 7.3H17.3" />
          <path d="M19.8 33.8c1.8 0 3.3 1.4 3.3 3.1s-1.5 3.1-3.3 3.1-3.2-1.4-3.2-3.1 1.4-3.1 3.2-3.1Z" />
          <path d="M34.8 33.8c1.8 0 3.3 1.4 3.3 3.1s-1.5 3.1-3.3 3.1-3.2-1.4-3.2-3.1 1.4-3.1 3.2-3.1Z" />
          <path d="M37.9 21.7h-5.2" />
          <path d="M10.2 8.2c1.4-.8 2.8-.8 4.2 0" />
        </svg>`;
    }

    return `
      <svg class="rw-promo-progress__icon-svg" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <path class="rw-promo-progress__icon-accent" d="M15.2 28.8c5.8 2.2 11.8 2.2 17.9 0v5.4c-5.9 2.2-11.9 2.2-17.9 0v-5.4Z" />
        <path d="M15.7 13.6 22 10.8c1.4 2 2.8 2.9 4.3 2.9s2.9-.9 4.3-2.9l6.3 2.8 4.1 7.7-5.4 2.9-2.5-3.6v17.1c-5.8 2.1-11.7 2.1-17.6 0V20.6L13 24.2l-5.4-2.9 4.1-7.7Z" />
        <path d="M21.9 11.1c.7 3.4 7.8 3.4 8.6 0" />
        <path d="M19.4 27.4c4.5 1.2 9.1 1.2 13.7 0" />
        <path d="M19.4 32.1c4.5 1.2 9.1 1.2 13.7 0" />
        <path d="M14.4 16.2c1.2.8 2.3.9 3.4.4" />
      </svg>`;
  };

  const renderItem = ({ type, label, message, percent, percentLabel, unlocked, note }) => `
    <div class="rw-promo-progress__item rw-promo-progress__item--${type}">
      <span class="rw-promo-progress__icon" aria-hidden="true">${icon(type)}</span>
      <div class="rw-promo-progress__content">
        <div class="rw-promo-progress__row">
          <div>
            <span class="rw-promo-progress__label${unlocked ? ' rw-promo-progress__label--unlocked' : ''}">${label}</span>
            <p class="rw-promo-progress__message">${message}</p>
          </div>
          <span class="rw-promo-progress__percent">${percentLabel}</span>
        </div>
        <div class="rw-promo-progress__track" aria-hidden="true"><span class="rw-promo-progress__fill" style="width:${percent}%"></span></div>
        ${note ? `<p class="rw-promo-progress__note">${note}</p>` : ''}
      </div>
    </div>`;

  const renderModule = (element, cart) => {
    const config = configFromElement(element);
    element.style.setProperty('--rw-progress-accent', config.accentColor);
    element.style.setProperty('--rw-progress-ink', config.inkColor);

    const eligible = eligibleQuantity(cart, config.eligibleKeyword);
    const shippingUnlocked = cart.total_price >= config.shippingThreshold;
    const promoUnlocked = eligible >= config.promoRequired;
    const shippingPercent = Math.min(100, Math.round((cart.total_price / config.shippingThreshold) * 100));
    const promoPercent = Math.min(100, Math.round((eligible / config.promoRequired) * 100));
    const promoCount = `${Math.min(eligible, config.promoRequired)}/${config.promoRequired}`;
    const context = element.dataset.context || '';
    const isDrawer = context === 'cart-drawer' || element.classList.contains('rw-promo-progress--drawer');

    element.innerHTML = `
      ${isDrawer ? '' : '<p class="rw-promo-progress__lede">GOOD NEWS</p>'}
      <div class="rw-promo-progress__grid">
        ${renderItem({
          type: 'shipping',
          label: 'FREE SHIPPING',
          message: shippingMessage(cart, config),
          percent: shippingPercent,
          percentLabel: `${shippingPercent}%`,
          unlocked: shippingUnlocked,
          note: isDrawer ? '' : config.shippingNote,
        })}
        ${renderItem({
          type: 'apparel',
          label: 'BUY 3 FREE 1',
          message: promoMessage(eligible, config),
          percent: promoPercent,
          percentLabel: isDrawer ? promoCount : `${promoPercent}%`,
          unlocked: promoUnlocked,
          note: '',
        })}
      </div>`;
  };

  const fetchCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());

  const ensureDrawerModule = () => {
    const drawerFooter = document.querySelector('cart-drawer .drawer__footer');
    if (!drawerFooter || drawerFooter.querySelector('readywear-promo-progress')) return;

    const config = getBaseConfig();
    const module = document.createElement('readywear-promo-progress');
    module.className = 'rw-promo-progress rw-promo-progress--drawer';
    module.dataset.context = 'cart-drawer';
    module.dataset.shippingThreshold = config.shippingThreshold;
    module.dataset.promoRequired = config.promoRequired;
    module.dataset.eligibleKeyword = config.eligibleKeyword;
    module.dataset.shippingNote = config.shippingNote;
    module.dataset.accentColor = config.accentColor;
    module.dataset.inkColor = config.inkColor;
    module.innerHTML = '<p class="rw-promo-progress__loading">Checking cart perks...</p>';

    drawerFooter.prepend(module);
  };

  const selectedVariant = () => {
    const script = document.querySelector('variant-selects script[data-selected-variant]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || 'null');
    } catch (error) {
      return null;
    }
  };

  const cleanOptionText = (element) => {
    if (!element) return '';
    const clone = element.cloneNode(true);
    clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
  };

  const selectedSize = () => {
    const optionGroups = Array.from(document.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input'));
    const sizeGroup = optionGroups.find((group) => {
      const label = group.querySelector('legend, .form__label, label');
      return label && /size/i.test(label.textContent || '');
    });

    if (!sizeGroup) return '';

    const select = sizeGroup.querySelector('select');
    if (select) return select.options[select.selectedIndex] ? select.options[select.selectedIndex].textContent.trim() : '';

    const checked = sizeGroup.querySelector('input:checked');
    if (!checked) return '';

    const optionLabel = sizeGroup.querySelector(`label[for="${checked.id}"]`);
    return cleanOptionText(optionLabel) || checked.value;
  };

  const initStickyAtc = () => {
    if (stickyInitialized) return;

    const sticky = document.querySelector('.rw-sticky-atc');
    const submitButton = document.querySelector('[id^="ProductSubmitButton-"]');
    if (!sticky || !submitButton) return;

    stickyInitialized = true;
    const productTitle =
      document.querySelector('.product__title h1')?.textContent.trim() ||
      document.querySelector('h1')?.textContent.trim() ||
      sticky.querySelector('.rw-sticky-atc__title')?.textContent.trim() ||
      'Readywear tee';

    sticky.classList.add('rw-sticky-atc--enhanced');
    sticky.innerHTML = `
      <div class="rw-sticky-atc__inner">
        <div class="rw-sticky-atc__summary">
          <p class="rw-sticky-atc__title"><strong></strong></p>
          <p class="rw-sticky-atc__size"></p>
          <p class="rw-sticky-atc__price"></p>
        </div>
        <button type="button" class="button rw-sticky-atc__button" data-rw-sticky-atc>Add to cart</button>
      </div>`;

    const title = sticky.querySelector('.rw-sticky-atc__title strong');
    const size = sticky.querySelector('.rw-sticky-atc__size');
    const price = sticky.querySelector('.rw-sticky-atc__price');
    const stickyButton = sticky.querySelector('[data-rw-sticky-atc]');
    const buyArea = submitButton.closest('.product-form__buttons') || submitButton.closest('product-form') || submitButton;

    const updateStickyContent = () => {
      const variant = selectedVariant();
      const currentSize = selectedSize();
      title.textContent = productTitle;
      size.textContent = currentSize ? `Size: ${currentSize}` : '';
      price.textContent = variant && typeof variant.price === 'number' ? money(variant.price) : '';
      stickyButton.disabled = submitButton.disabled;
      stickyButton.textContent = submitButton.disabled ? submitButton.textContent.trim() || 'Sold out' : 'Add to cart';
    };

    const updateStickyVisibility = () => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const rect = buyArea.getBoundingClientRect();
      const shouldShow = isMobile && rect.bottom < 0;
      sticky.classList.toggle('rw-sticky-atc--visible', shouldShow);
      document.body.classList.toggle('rw-sticky-atc-is-visible', shouldShow);
    };

    stickyButton.addEventListener('click', () => submitButton.click());
    document.addEventListener('change', (event) => {
      if (event.target.closest('variant-selects')) window.setTimeout(updateStickyContent, 80);
    });
    document.addEventListener('shopify:section:load', () => window.setTimeout(initStickyAtc, 100));
    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', updateStickyVisibility);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(updateStickyVisibility, { threshold: [0, 1] });
      observer.observe(buyArea);
    }

    updateStickyContent();
    updateStickyVisibility();
  };

  const refresh = () => {
    ensureDrawerModule();
    initStickyAtc();
    const modules = document.querySelectorAll('readywear-promo-progress');
    if (!modules.length) return;

    fetchCart()
      .then((cart) => modules.forEach((module) => renderModule(module, cart)))
      .catch(() => {
        modules.forEach((module) => {
          module.innerHTML = '<p class="rw-promo-progress__loading">Cart perks will update after add to cart.</p>';
        });
      });
  };

  const scheduleRefresh = (delay = 250) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay);
  };

  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[action*="/cart/add"]')) scheduleRefresh(900);
  });

  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(300));
  }
})();
